import { EvaluationGoal, SupplierType } from "../../types/BuyerBriefTypes";

export interface TopicOption {
    id: string;
    label: string;
    description: string;
    forTypes?: SupplierType[]; // Supplier type-specific topics
}

enum TopicId {
    FINANCIAL = "financial_stability",
    LEGAL = "legal_regulatory_exposure",
    RISK = "risk_assessment",
    PROFILE = "company_profile",
    COMPETITIVE = "competitive_landscape",
    REPUTATION = "customer_reputation",
    INNOVATION = "innovation_roadmap",
    PRICING = "pricing_total_cost",
    LOCKIN = "contract_lockin_risk",
    SUPPORT = "relationship_support",
    LEVERAGE = "negotiation_leverage",
    SECURITY = "security_data_protection",
    UPTIME = "uptime_reliability",
    PRODUCT = "product_capabilities",
    SUPPLY_CHAIN = "supply_chain_capacity",
    QUALITY = "quality_certifications",
    DELIVERY = "delivery_expertise",
    REGULATORY = "regulatory_compliance",
    SAFETY = "safety_environmental",
    ESG = "esg_sustainability",
    RETENTION = "customer_retention_signals",
    CASESTUDIES = "case_studies_references",
    MA = "ma_ownership_risk",
}

// Financial & Risk Intelligence
export const FINANCIAL_TOPICS: TopicOption[] = [
    {
        id: TopicId.FINANCIAL,
        label: "Financial Stability",
        description: "Revenue trends, cash flow, debt, profitability, and going-concern signals",
    },
    {
        id: TopicId.LEGAL,
        label: "Legal & Regulatory Exposure",
        description: "Active litigation, SEC actions, regulatory fines, and compliance violations",
    },
    {
        id: TopicId.RISK,
        label: "Risk Assessment",
        description:
            "Concentration risk, supply chain exposure, geopolitical factors, and key-person dependencies",
    },
];

// Market & Competitive Position

export const MARKET_TOPICS: TopicOption[] = [
    {
        id: TopicId.PROFILE,
        label: "Company Profile",
        description:
            "History, size, leadership stability, ownership structure, and strategic direction",
    },
    {
        id: TopicId.COMPETITIVE,
        label: "Competitive Landscape",
        description: "Market share, key competitors, differentiation, and switching alternatives",
    },
    {
        id: TopicId.REPUTATION,
        label: "Customer Reputation",
        description: "Review sentiment, NPS signals, public complaints, and reference quality",
    },
    {
        id: TopicId.INNOVATION,
        label: "Innovation & Roadmap",
        description: "R&D investment, product trajectory, technology moat, and future-proofing",
    },
];

// Pricing & Commercial Terms
export const PRICING_TOPICS: TopicOption[] = [
    {
        id: TopicId.PRICING,
        label: "Pricing & Total Cost",
        description: "Pricing models, hidden costs, TCO benchmarks, and cost optimization levers",
    },
    {
        id: TopicId.LOCKIN,
        label: "Contract & Lock-in Risk",
        description: "Exit clauses, data portability, migration support, and vendor dependency",
    },
    {
        id: TopicId.SUPPORT,
        label: "Relationship & Support",
        description:
            "Account management quality, SLA enforcement, escalation paths, and responsiveness",
    },
    {
        id: TopicId.LEVERAGE,
        label: "Negotiation Leverage",
        description:
            "Discount benchmarks, competitive alternatives, contract term flexibility, and renewal timing",
    },
];

// Core topics - always visible
export const CORE_TOPICS: TopicOption[] = [
    ...FINANCIAL_TOPICS,
    ...MARKET_TOPICS,
    ...PRICING_TOPICS,
];

// Type-specific topics - shown based on supplier type
export const TYPE_TOPICS: TopicOption[] = [
    {
        id: TopicId.SECURITY,
        label: "Security & Data Protection",
        description: "SOC 2, ISO 27001, GDPR, breach history, encryption, and access controls",
        forTypes: [
            SupplierType.SOFTWARE_SAAS,
            SupplierType.CLOUD_INFRASTRUCTURE,
            SupplierType.CYBERSECURITY,
        ],
    },
    {
        id: TopicId.UPTIME,
        label: "Uptime & Reliability",
        description:
            "SLA history, incident track record, infrastructure redundancy, and DR posture",
        forTypes: [
            SupplierType.SOFTWARE_SAAS,
            SupplierType.CLOUD_INFRASTRUCTURE,
            SupplierType.TELECOM_CONNECTIVITY,
            SupplierType.CYBERSECURITY,
        ],
    },
    {
        id: TopicId.PRODUCT,
        label: "Product Capabilities",
        description: "Core features, integrations, API ecosystem, and platform maturity",
        forTypes: [
            SupplierType.SOFTWARE_SAAS,
            SupplierType.CLOUD_INFRASTRUCTURE,
            SupplierType.CYBERSECURITY,
            SupplierType.PAYROLL_HR_TECH,
        ],
    },
    {
        id: TopicId.SUPPLY_CHAIN,
        label: "Supply Chain & Capacity",
        description:
            "Production capacity, lead times, logistics network, and fulfillment reliability",
        forTypes: [
            SupplierType.MANUFACTURING_INDUSTRIAL,
            SupplierType.RAW_MATERIALS_COMMODITIES,
            SupplierType.LOGISTICS_DISTRIBUTION,
            SupplierType.FLEET_TRANSPORTATION,
            SupplierType.FOOD_BEVERAGE,
            SupplierType.ENERGY_UTILITIES,
            SupplierType.CONSTRUCTION_ENGINEERING,
        ],
    },
    {
        id: TopicId.QUALITY,
        label: "Quality & Certifications",
        description:
            "ISO standards, quality control processes, defect rates, and industry certifications",
        forTypes: [
            SupplierType.MANUFACTURING_INDUSTRIAL,
            SupplierType.RAW_MATERIALS_COMMODITIES,
            SupplierType.LOGISTICS_DISTRIBUTION,
            SupplierType.CONSTRUCTION_ENGINEERING,
            SupplierType.ENERGY_UTILITIES,
            SupplierType.FOOD_BEVERAGE,
        ],
    },
    {
        id: TopicId.DELIVERY,
        label: "Delivery & Expertise",
        description: "Team qualifications, methodology, case outcomes, and domain expertise depth",
        forTypes: [
            SupplierType.CONSULTING_ADVISORY,
            SupplierType.LEGAL_SERVICES,
            SupplierType.STAFFING_RECRUITMENT,
            SupplierType.MARKETING_ADVERTISING,
            SupplierType.FINANCIAL_SERVICES,
            SupplierType.TRAINING_DEVELOPMENT,
        ],
    },
    {
        id: TopicId.REGULATORY,
        label: "Regulatory Compliance",
        description: "HIPAA, ACA, ERISA, fiduciary standards, and audit readiness",
        forTypes: [
            SupplierType.BENEFITS_INSURANCE,
            SupplierType.HEALTHCARE_LIFE_SCIENCES,
            SupplierType.PAYROLL_HR_TECH,
        ],
    },
    {
        id: TopicId.SAFETY,
        label: "Safety & Environmental",
        description:
            "OSHA record, safety certifications, environmental permits, and incident history",
        forTypes: [
            SupplierType.FACILITIES_REAL_ESTATE,
            SupplierType.CONSTRUCTION_ENGINEERING,
            SupplierType.FLEET_TRANSPORTATION,
        ],
    },
];

// Deep dive topics - never auto-selected
export const DEEP_DIVE_TOPICS: TopicOption[] = [
    {
        id: TopicId.ESG,
        label: "ESG & Sustainability",
        description:
            "Carbon footprint, DEI metrics, governance practices, and sustainability commitments",
    },
    {
        id: TopicId.RETENTION,
        label: "Customer Retention Signals",
        description: "Churn indicators, renewal rates, logo losses, and satisfaction trends",
    },
    {
        id: TopicId.CASESTUDIES,
        label: "Case Studies & References",
        description: "Published wins, reference customers, testimonials, and documented outcomes",
    },
    {
        id: TopicId.MA,
        label: "M&A & Ownership Risk",
        description:
            "Acquisition likelihood, PE/VC ownership pressure, divestitures, and leadership churn signals",
    },
];

// All topics combined
export const ALL_TOPICS = [...CORE_TOPICS, ...TYPE_TOPICS, ...DEEP_DIVE_TOPICS];

// Get topics visible for a given supplier type
export function getTopicsForType(category: SupplierType | undefined): TopicOption[] {
    if (!category) return CORE_TOPICS;
    return [...CORE_TOPICS, ...TYPE_TOPICS.filter((t) => t.forTypes?.includes(category))];
}

// Goal → topic recommendation mapping
export interface GoalRecommendation {
    on: string[]; // topic IDs to turn ON
    off: string[]; // topic IDs to turn OFF
    banner: string; // recommendation text
}

export const GOAL_RECOMMENDATIONS: Record<string, GoalRecommendation> = {
    [EvaluationGoal.NEW_SUPPLIER_EVALUATION]: {
        on: [
            TopicId.FINANCIAL,
            TopicId.LEGAL,
            TopicId.RISK,
            TopicId.PROFILE,
            TopicId.COMPETITIVE,
            TopicId.REPUTATION,
            TopicId.INNOVATION,
            TopicId.PRICING,
            TopicId.LOCKIN,
            TopicId.SUPPORT,
            TopicId.LEVERAGE,
            TopicId.SECURITY,
            TopicId.UPTIME,
            TopicId.PRODUCT,
        ],
        off: [],
        banner: "broad coverage across financial health, market position, and commercial terms to build a complete picture.",
    },
    [EvaluationGoal.RENEWAL_DECISION]: {
        on: [
            TopicId.FINANCIAL,
            TopicId.LEGAL,
            TopicId.RISK,
            TopicId.PRICING,
            TopicId.LOCKIN,
            TopicId.SUPPORT,
            TopicId.LEVERAGE,
            TopicId.REPUTATION,
        ],
        off: [TopicId.PROFILE, TopicId.INNOVATION],
        banner: "focused on pricing trends, lock-in risk, support quality, and customer reputation to inform your renewal position.",
    },
    [EvaluationGoal.COMPETITIVE_COMPARISON]: {
        on: [
            TopicId.PROFILE,
            TopicId.COMPETITIVE,
            TopicId.REPUTATION,
            TopicId.PRICING,
            TopicId.INNOVATION,
            TopicId.PRODUCT,
            TopicId.LEVERAGE,
        ],
        off: [TopicId.LEGAL, TopicId.RISK, TopicId.LOCKIN, TopicId.SUPPORT],
        banner: "emphasis on market positioning, product capabilities, pricing, and differentiation vs. alternatives.",
    },
    [EvaluationGoal.CONTRACT_NEGOTIATION_PREP]: {
        on: [
            TopicId.PRICING,
            TopicId.LEVERAGE,
            TopicId.LOCKIN,
            TopicId.COMPETITIVE,
            TopicId.FINANCIAL,
            TopicId.SUPPORT,
        ],
        off: [TopicId.PROFILE, TopicId.INNOVATION, TopicId.LEGAL, TopicId.RISK, TopicId.REPUTATION],
        banner: "heavy focus on pricing benchmarks, negotiation leverage, lock-in risk, and competitive alternatives for maximum leverage.",
    },
    [EvaluationGoal.RISK_ASSESSMENT]: {
        on: [
            TopicId.FINANCIAL,
            TopicId.LEGAL,
            TopicId.RISK,
            TopicId.LOCKIN,
            TopicId.REPUTATION,
            TopicId.SECURITY,
            TopicId.UPTIME,
        ],
        off: [
            TopicId.PROFILE,
            TopicId.COMPETITIVE,
            TopicId.INNOVATION,
            TopicId.PRICING,
            TopicId.LEVERAGE,
            TopicId.SUPPORT,
        ],
        banner: "deep dive into financial stability, legal exposure, security posture, and ownership risk to surface red flags.",
    },
    [EvaluationGoal.DUE_DILIGENCE]: {
        on: [
            TopicId.FINANCIAL,
            TopicId.LEGAL,
            TopicId.RISK,
            TopicId.PROFILE,
            TopicId.REPUTATION,
            TopicId.LOCKIN,
            TopicId.SECURITY,
            TopicId.UPTIME,
        ],
        off: [
            TopicId.COMPETITIVE,
            TopicId.INNOVATION,
            TopicId.PRICING,
            TopicId.LEVERAGE,
            TopicId.SUPPORT,
        ],
        banner: "comprehensive risk and compliance review including financials, legal exposure, security certifications, and ownership risk.",
    },
};

// Deep dive IDs - never auto-selected
export const DEEP_DIVE_IDS = DEEP_DIVE_TOPICS.map((t) => t.id);
